import {
  AuthenticatorId,
  GenerationId,
  ProfileId,
  ProfileKeyGenerationId,
  VaultId,
  VaultType,
  UserId,
  OrganizationId,
  LoginId,
} from 'backend-client-web';
import { DomainError, ErrorGroup } from 'domain-errors';

enum ClientCoreErrorGroup {
  Generic = 10000,
  Vault = 20000,
  Authenticator = 30000,
  Syncing = 40000,
  Core = 50000,
  Login = 60000,
  Protected = 70000,
  Organization = 80000,
  Profile = 90000,
  Webauthn = 100000,
  ShareLink = 110000,
  ShareSecret = 120000,
  Passkey = 130000,
}

export enum ClientCoreErrorCode {
  // Generic
  NotImplemented = ErrorGroup.ClientCore + ClientCoreErrorGroup.Generic + 500,
  Internal = ErrorGroup.ClientCore + ClientCoreErrorGroup.Generic + 501,
  InitialSyncRequired = ErrorGroup.ClientCore + ClientCoreErrorGroup.Generic + 502,
  MalformedFramedData = ErrorGroup.ClientCore + ClientCoreErrorGroup.Generic + 503,
  PublicDataServerNotConfigured = ErrorGroup.ClientCore + ClientCoreErrorGroup.Generic + 504,
  PublicDataUnavailable = ErrorGroup.ClientCore + ClientCoreErrorGroup.Generic + 505,
  // no longer in use
  // MetaVaultOnly = ErrorGroup.ClientCore + ClientCoreErrorGroup.Generic + 510,

  // Vault
  ParentIdDoesntMatch = ErrorGroup.ClientCore + ClientCoreErrorGroup.Vault + 401,
  CannotOverrideCommitId = ErrorGroup.ClientCore + ClientCoreErrorGroup.Vault + 402,

  InvalidSessionModification = ErrorGroup.ClientCore + ClientCoreErrorGroup.Vault + 410,
  InvalidVaultContentDescriptorName = ErrorGroup.ClientCore + ClientCoreErrorGroup.Vault + 411,
  LoginNotFound = ErrorGroup.ClientCore + ClientCoreErrorGroup.Vault + 412,

  CannotRegenerateLockedVault = ErrorGroup.ClientCore + ClientCoreErrorGroup.Vault + 420,
  UserAuthenticatorsMissingForRegenerate = ErrorGroup.ClientCore + ClientCoreErrorGroup.Vault + 421,
  ProfileMissingForRegenerate = ErrorGroup.ClientCore + ClientCoreErrorGroup.Vault + 422,

  NoCommits = ErrorGroup.ClientCore + ClientCoreErrorGroup.Vault + 430,
  NeedLocks = ErrorGroup.ClientCore + ClientCoreErrorGroup.Vault + 431,
  NeedFullHistory = ErrorGroup.ClientCore + ClientCoreErrorGroup.Vault + 432,

  VaultIsLocked = ErrorGroup.ClientCore + ClientCoreErrorGroup.Vault + 440,

  MetaVaultIdDoesntMatch = ErrorGroup.ClientCore + ClientCoreErrorGroup.Vault + 450,
  MetaVaultNotFound = ErrorGroup.ClientCore + ClientCoreErrorGroup.Vault + 451,
  VaultNotFound = ErrorGroup.ClientCore + ClientCoreErrorGroup.Vault + 452,
  // no longer in use
  // InvalidVaultType = ErrorGroup.ClientCore + ClientCoreErrorGroup.Vault + 453,
  // InvalidVaultTypeLoginVault = ErrorGroup.ClientCore + ClientCoreErrorGroup.Vault + 454,
  // InvalidVaultTypeMetaVault = ErrorGroup.ClientCore + ClientCoreErrorGroup.Vault + 455,
  PrivateLoginVaultNotFound = ErrorGroup.ClientCore + ClientCoreErrorGroup.Vault + 456,
  // InvalidVaultOperation = ErrorGroup.ClientCore + ClientCoreErrorGroup.Vault + 457,
  // no longer in use
  // InvalidVaultTypeTeamMetaVault = ErrorGroup.ClientCore + ClientCoreErrorGroup.Vault + 458,
  IncorrectVaultTypeError = ErrorGroup.ClientCore + ClientCoreErrorGroup.Vault + 459,

  InvalidCommitStructure = ErrorGroup.ClientCore + ClientCoreErrorGroup.Vault + 460,
  VaultContentTooNew = ErrorGroup.ClientCore + ClientCoreErrorGroup.Vault + 461,
  InvalidCommitContent = ErrorGroup.ClientCore + ClientCoreErrorGroup.Vault + 462,
  MergeConflict = ErrorGroup.ClientCore + ClientCoreErrorGroup.Vault + 463,
  UnsupportedCompression = ErrorGroup.ClientCore + ClientCoreErrorGroup.Vault + 464,
  UnsupportedVaultVersion = ErrorGroup.ClientCore + ClientCoreErrorGroup.Vault + 465,
  VaultIsNotEmpty = ErrorGroup.ClientCore + ClientCoreErrorGroup.Vault + 466,

  TeamVaultMissingAssociatedMetaVault = ErrorGroup.ClientCore + ClientCoreErrorGroup.Vault + 470,
  VaultPermissionDenied = ErrorGroup.ClientCore + ClientCoreErrorGroup.Vault + 471,
  UnsupportedLoginVaultType = ErrorGroup.ClientCore + ClientCoreErrorGroup.Vault + 472,
  OrganizationPersonalVaultNotFound = ErrorGroup.ClientCore + ClientCoreErrorGroup.Vault + 473,
  VaultLoginPermissionDenied = ErrorGroup.ClientCore + ClientCoreErrorGroup.Vault + 474,

  VaultLinkingNotSupported = ErrorGroup.ClientCore + ClientCoreErrorGroup.Vault + 480,
  VaultMessageEncryptedDataNotFound = ErrorGroup.ClientCore + ClientCoreErrorGroup.Vault + 490,

  // Authenticator
  LockAuthenticatorNotFound = ErrorGroup.ClientCore + ClientCoreErrorGroup.Authenticator + 400,

  // no longer in use
  // InvalidAuthenticatorType = ErrorGroup.ClientCore + ClientCoreErrorGroup.Authenticator + 410,

  UnlockedAuthenticatorNotFound = ErrorGroup.ClientCore + ClientCoreErrorGroup.Authenticator + 420,

  LoginWithRecovery = ErrorGroup.ClientCore + ClientCoreErrorGroup.Authenticator + 430,

  RemoveLocalAuthenticator = ErrorGroup.ClientCore + ClientCoreErrorGroup.Authenticator + 440,

  LazyWithoutGetter = ErrorGroup.ClientCore + ClientCoreErrorGroup.Authenticator + 450,
  LazyWithSetter = ErrorGroup.ClientCore + ClientCoreErrorGroup.Authenticator + 451,
  WrongAuthenticatorSecret = ErrorGroup.ClientCore + ClientCoreErrorGroup.Authenticator + 452,

  SecretReuse = ErrorGroup.ClientCore + ClientCoreErrorGroup.Authenticator + 460,

  InvalidRecoveryCode = ErrorGroup.ClientCore + ClientCoreErrorGroup.Authenticator + 461,
  InvalidAuthenticatorSeedLength = ErrorGroup.ClientCore + ClientCoreErrorGroup.Authenticator + 462,

  AuthenticatorSecreMissingId = ErrorGroup.ClientCore + ClientCoreErrorGroup.Authenticator + 470,
  AuthenticatorNotFound = ErrorGroup.ClientCore + ClientCoreErrorGroup.Authenticator + 471,

  AuthenticatorBlockNotInitial = ErrorGroup.ClientCore + ClientCoreErrorGroup.Authenticator + 480,
  AuthenticatorBlockNotChild = ErrorGroup.ClientCore + ClientCoreErrorGroup.Authenticator + 481,
  AuthenticatorBlockInvalidSigningKey = ErrorGroup.ClientCore +
    ClientCoreErrorGroup.Authenticator +
    482,

  NoMatchingSigningKeyFound = ErrorGroup.ClientCore + ClientCoreErrorGroup.Authenticator + 490,

  NoAuthenticatorIdReceived = ErrorGroup.ClientCore + ClientCoreErrorGroup.Authenticator + 500,

  // Syncing
  UnlockRejected = ErrorGroup.ClientCore + ClientCoreErrorGroup.Syncing + 401,
  InternalRecoveredSync = ErrorGroup.ClientCore + ClientCoreErrorGroup.Syncing + 402,
  CentrifugeError = ErrorGroup.ClientCore + ClientCoreErrorGroup.Syncing + 403,
  FailedPushingCommit = ErrorGroup.ClientCore + ClientCoreErrorGroup.Syncing + 410,

  // Core
  LoginDataMissing = ErrorGroup.ClientCore + ClientCoreErrorGroup.Core + 400,
  OwnSessionMissing = ErrorGroup.ClientCore + ClientCoreErrorGroup.Core + 401,
  ClientCoreIsLocked = ErrorGroup.ClientCore + ClientCoreErrorGroup.Core + 440,
  PersistableMismatchError = ErrorGroup.ClientCore + ClientCoreErrorGroup.Core + 500,

  // Login
  LoginNotInitiated = ErrorGroup.ClientCore + ClientCoreErrorGroup.Login + 400,

  PushFlowRunning = ErrorGroup.ClientCore + ClientCoreErrorGroup.Login + 410,
  ChannelIdMissing = ErrorGroup.ClientCore + ClientCoreErrorGroup.Login + 411,

  InvalidHashCommitment = ErrorGroup.ClientCore + ClientCoreErrorGroup.Login + 420,
  LoginCancelled = ErrorGroup.ClientCore + ClientCoreErrorGroup.Login + 421,

  UnknownAuthenticatorReply = ErrorGroup.ClientCore + ClientCoreErrorGroup.Login + 430,

  // Protected (Secret)
  ProtectInternalError = ErrorGroup.ClientCore + ClientCoreErrorGroup.Protected + 500,

  CantProtect = ErrorGroup.ClientCore + ClientCoreErrorGroup.Protected + 401,
  CantUnprotect = ErrorGroup.ClientCore + ClientCoreErrorGroup.Protected + 402,
  CantCreateLock = ErrorGroup.ClientCore + ClientCoreErrorGroup.Protected + 403,
  CantDecryptVaultMessage = ErrorGroup.ClientCore + ClientCoreErrorGroup.Protected + 404,
  VaultProtectedSecretNotFound = ErrorGroup.ClientCore + ClientCoreErrorGroup.Protected + 405,

  // Organizations
  OrganizationNotFoundError = ErrorGroup.ClientCore + ClientCoreErrorGroup.Organization + 400,
  NoOrganizationMemberError = ErrorGroup.ClientCore + ClientCoreErrorGroup.Organization + 401,
  NoOrganizationAdminError = ErrorGroup.ClientCore + ClientCoreErrorGroup.Organization + 402,
  AdminVaultNotFoundError = ErrorGroup.ClientCore + ClientCoreErrorGroup.Organization + 403,
  NoOrganizationAdminProfileError = ErrorGroup.ClientCore + ClientCoreErrorGroup.Organization + 404,
  RoleNotFoundError = ErrorGroup.ClientCore + ClientCoreErrorGroup.Organization + 405,

  // Profiles
  LockProfileNotFound = ErrorGroup.ClientCore + ClientCoreErrorGroup.Profile + 400,
  ProfileKeyGenerationMismatch = ErrorGroup.ClientCore + ClientCoreErrorGroup.Profile + 401,
  NoMatchingProfileFound = ErrorGroup.ClientCore + ClientCoreErrorGroup.Profile + 402,
  ProfileNotFoundError = ErrorGroup.ClientCore + ClientCoreErrorGroup.Profile + 404,
  MissingProfileProfileLock = ErrorGroup.ClientCore + ClientCoreErrorGroup.Profile + 405,

  // Webauthn
  PrfNotSupportedError = ErrorGroup.ClientCore + ClientCoreErrorGroup.Webauthn + 501,
  PrfInitError = ErrorGroup.ClientCore + ClientCoreErrorGroup.Webauthn + 502,

  // Share links
  ShareLinkNotFound = ErrorGroup.ClientCore + ClientCoreErrorGroup.ShareLink + 400,
  InvalidShareLink = ErrorGroup.ClientCore + ClientCoreErrorGroup.ShareLink + 401,

  // Share secrets
  ShareSecretNotFound = ErrorGroup.ClientCore + ClientCoreErrorGroup.ShareLink + 400,
  InvalidShareSecret = ErrorGroup.ClientCore + ClientCoreErrorGroup.ShareLink + 401,
  InvalidAgeRecipient = ErrorGroup.ClientCore + ClientCoreErrorGroup.ShareLink + 402,

  // Passkey
  NotAPasskeyLogin = ErrorGroup.ClientCore + ClientCoreErrorGroup.Passkey + 400,
}

const pleaseUpdate =
  'We are investigating. In the meantime, make sure you are using the latest version of our software then try again.';

export class ClientCoreDomainError extends DomainError {
  constructor(code: ClientCoreErrorCode, short: string, userTitle?: string, userDetail?: string) {
    super(code, short, userTitle ?? short, userDetail ?? pleaseUpdate);
  }
}

export class NotImplementedError extends ClientCoreDomainError {
  constructor(featureName: string) {
    super(
      ClientCoreErrorCode.NotImplemented,
      `The feature "${featureName}" is not implemented yet`,
    );
  }
}

export class ClientCoreInternalError extends ClientCoreDomainError {
  constructor(message: string) {
    super(ClientCoreErrorCode.Internal, `Internal error: ${message}`);
  }
}

export class InitialSyncRequiredError extends ClientCoreDomainError {
  constructor() {
    super(
      ClientCoreErrorCode.InitialSyncRequired,
      'ClientCore must have performed an initial sync for this operation!',
    );
  }
}

export class MalformedFramedDataError extends ClientCoreDomainError {
  constructor() {
    super(ClientCoreErrorCode.MalformedFramedData, 'Framed data is truncated or malformed!');
  }
}

export class PublicDataServerNotConfiguredError extends ClientCoreDomainError {
  constructor() {
    super(ClientCoreErrorCode.PublicDataServerNotConfigured, 'No public data server configured!');
  }
}

export class PublicDataUnavailableError extends ClientCoreDomainError {
  constructor(message: string) {
    super(ClientCoreErrorCode.PublicDataUnavailable, `Public data unavailable: ${message}`);
  }
}

export class PersistableMismatchError extends ClientCoreDomainError {
  constructor(persistableUserId: UserId, ccpUserId: UserId) {
    super(
      ClientCoreErrorCode.PersistableMismatchError,
      `Persisted user did not match login parameters! (persistable: ${persistableUserId}, ccp: ${ccpUserId})`,
    );
  }
}

export class ParentIdDoesntMatchError extends ClientCoreDomainError {
  constructor(want: string, got: string | undefined) {
    super(
      ClientCoreErrorCode.ParentIdDoesntMatch,
      `Commit parentId doesn't match: expected ${want}, got ${got}`,
      `Commit parentId doesn't match: expected ${want}, got ${got}`,
      `The vault data loaded seems to be inconsistent. Try to re-login. ${pleaseUpdate}`,
    );
  }
}

// TODO: add userTitle and userDetail
export class CannotOverrideCommitIdError extends ClientCoreDomainError {
  constructor() {
    super(
      ClientCoreErrorCode.CannotOverrideCommitId,
      'Cannot override commitId if not in staging state!',
      '',
      '',
    );
  }
}

export class CannotProtectError extends ClientCoreDomainError {
  constructor() {
    super(
      ClientCoreErrorCode.CantProtect,
      "Can't protect while locked!",
      "Can't protect while locked!",
      `Tried to access a protected value while not logged in. Log in using the app, then try again. ${pleaseUpdate}`,
    );
  }
}

export class CannotUnprotectError extends ClientCoreDomainError {
  constructor() {
    super(
      ClientCoreErrorCode.CantUnprotect,
      "Can't unprotect while locked!",
      "Can't unprotect while locked!",
      `Tried to access a protected value while not logged in. Log in using the app, then try again. ${pleaseUpdate}`,
    );
  }
}

export class CannotCreateLockError extends ClientCoreDomainError {
  constructor() {
    super(ClientCoreErrorCode.CantCreateLock, "Can't create lock while locked!");
  }
}

export class ProtectInternalError extends ClientCoreDomainError {
  constructor(detail: string) {
    super(
      ClientCoreErrorCode.ProtectInternalError,
      detail,
      detail,
      `The client produced an internal error while trying to access a protected value. ${pleaseUpdate}`,
    );
  }
}

export class CannotDecryptVaultMessageError extends ClientCoreDomainError {
  constructor(detail = 'Cannot decrypt vault message') {
    super(ClientCoreErrorCode.CantDecryptVaultMessage, detail);
  }
}

export class VaultProtectedSecretNotFoundError extends ClientCoreDomainError {
  constructor() {
    super(
      ClientCoreErrorCode.VaultProtectedSecretNotFound,
      'Vault secret not found',
      'Vault secret not found',
      `The client is trying to access protected values of a vault with no present protected secret. ${pleaseUpdate}`,
    );
  }
}

// TODO: add userTitle and userDetail
export class InvalidSessionModificationError extends ClientCoreDomainError {
  constructor() {
    super(
      ClientCoreErrorCode.InvalidSessionModification,
      'Session modification led to invalid vault content!',
    );
  }
}

// TODO: add userTitle and userDetail
export class InvalidVaultContentDescriptorNameError extends ClientCoreDomainError {
  constructor(typeName: string) {
    super(
      ClientCoreErrorCode.InvalidVaultContentDescriptorName,
      `Unknown type descriptor ${typeName}`,
    );
  }
}

// TODO: add userTitle and userDetail
export class LoginNotFoundError extends ClientCoreDomainError {
  constructor(id: string) {
    super(ClientCoreErrorCode.LoginNotFound, `No login with id "${id}"!`);
  }
}

// TODO: add userTitle and userDetail
export class CannotRegenerateLockedVaultError extends ClientCoreDomainError {
  constructor() {
    super(ClientCoreErrorCode.CannotRegenerateLockedVault, "Can't regenerate a locked vault!");
  }
}

// TODO: add userTitle and userDetail
export class UserAuthenticatorsMissingForRegenerateError extends ClientCoreDomainError {
  constructor() {
    super(
      ClientCoreErrorCode.UserAuthenticatorsMissingForRegenerate,
      "Can't regenerate, authenticators are not presents for all users of the vault!",
    );
  }
}

// TODO: add userTitle and userDetail
export class ProfileMissingForRegenerateError extends ClientCoreDomainError {
  constructor() {
    super(
      ClientCoreErrorCode.ProfileMissingForRegenerate,
      "Can't regenerate, not all profiles present for the vault!",
    );
  }
}

export class LockAuthenticatorNotFoundError extends ClientCoreDomainError {
  constructor(authId: AuthenticatorId, locks: { authenticatorId: AuthenticatorId }[]) {
    super(
      ClientCoreErrorCode.LockAuthenticatorNotFound,
      `Found no matching lock for available authenticator! Locally available authenticator id ${authId}, lock authenticator ids: ${locks.map(
        (l) => l.authenticatorId,
      )}`,
    );
  }
}

export class LockProfileNotFoundError extends ClientCoreDomainError {
  constructor(profileId: ProfileId) {
    super(
      ClientCoreErrorCode.LockProfileNotFound,
      `Got no matching lock for available profiles! Lock profile id: ${profileId}`,
    );
  }
}

export class ProfileKeyGenerationMismatchError extends ClientCoreDomainError {
  constructor(
    profileId: ProfileId,
    profileGenId: ProfileKeyGenerationId,
    lockGenId: ProfileKeyGenerationId,
  ) {
    super(
      ClientCoreErrorCode.ProfileKeyGenerationMismatch,
      `Mismatched keyGenerationId, profileId: ${profileId}, profile keyGenId: ${profileGenId}, lock keyGenId: ${lockGenId}`,
    );
  }
}

export class NoMatchingProfileFoundError extends ClientCoreDomainError {
  constructor(userId: UserId, origanizationId: OrganizationId | undefined) {
    super(
      ClientCoreErrorCode.NoMatchingProfileFound,
      `No matching profile found, userId: ${userId}, organizationId: ${origanizationId}`,
    );
  }
}

export class ProfileNotFoundError extends ClientCoreDomainError {
  constructor(profileId: ProfileId) {
    super(ClientCoreErrorCode.ProfileNotFoundError, `Profile ${profileId} unexpectedly not loaded`);
  }
}

export class MissingProfileProfileLockError extends ClientCoreDomainError {
  constructor() {
    super(ClientCoreErrorCode.MissingProfileProfileLock, `Missing profile profile lock`);
  }
}

export class NoCommitsError extends ClientCoreDomainError {
  constructor() {
    super(ClientCoreErrorCode.NoCommits, 'Cannot load vault with no commits');
  }
}

export class NeedLocksError extends ClientCoreDomainError {
  constructor(vaultId: VaultId, oldGenId: GenerationId, newGenId: GenerationId) {
    super(
      ClientCoreErrorCode.NeedLocks,
      `Unexpectedly didn't receive locks (vault ${vaultId}, local gen ${oldGenId}, new gen ${newGenId})`,
    );
  }
}

export class NeedFullHistoryError extends ClientCoreDomainError {
  constructor(vaultId: VaultId, oldGenId: GenerationId, newGenId: GenerationId) {
    super(
      ClientCoreErrorCode.NeedFullHistory,
      `Unexpectedly didn't receive full history (vault ${vaultId}, local gen ${oldGenId}, new gen ${newGenId})`,
    );
  }
}

export class UnlockedAuthenticatorNotFoundError extends ClientCoreDomainError {
  constructor() {
    super(
      ClientCoreErrorCode.UnlockedAuthenticatorNotFound,
      'Provided unlock does not match any known authenticator',
    );
  }
}

export class UnlockRejectedError extends ClientCoreDomainError {
  constructor() {
    super(ClientCoreErrorCode.UnlockRejected, 'Unlock request rejected');
  }
}

export class LoginWithRecoveryError extends ClientCoreDomainError {
  constructor() {
    super(
      ClientCoreErrorCode.LoginWithRecovery,
      "Can't perform a regular login with recovery authenticator!",
    );
  }
}

export class LoginDataMissingError extends ClientCoreDomainError {
  constructor() {
    super(ClientCoreErrorCode.LoginDataMissing, 'Login Data is missing!');
  }
}

export class OwnSessionMissingError extends ClientCoreDomainError {
  constructor() {
    super(
      ClientCoreErrorCode.OwnSessionMissing,
      'Missing our own session. This should never happen, expected DomainError!',
    );
  }
}

export class ClientCoreIsLockedError extends ClientCoreDomainError {
  constructor() {
    super(ClientCoreErrorCode.ClientCoreIsLocked, 'Tried to modify a locked ClientCore');
  }
}

export class FailedPushingCommitError extends ClientCoreDomainError {
  constructor() {
    super(ClientCoreErrorCode.FailedPushingCommit, 'Failed pusing commit!');
  }
}

export class VaultIsLockedError extends ClientCoreDomainError {
  constructor() {
    super(
      ClientCoreErrorCode.VaultIsLocked,
      "StagedDiff can't be re-encrypted while vault is locked",
    );
  }
}

export class RemoveLocalAuthenticatorError extends ClientCoreDomainError {
  constructor() {
    super(
      ClientCoreErrorCode.RemoveLocalAuthenticator,
      'Cannot remove local unlocked authenticator!',
    );
  }
}

export class LazyWithoutGetterError extends ClientCoreDomainError {
  constructor() {
    super(ClientCoreErrorCode.LazyWithoutGetter, 'Lazy is only applicable for getters!');
  }
}

export class LazyWithSetterError extends ClientCoreDomainError {
  constructor() {
    super(ClientCoreErrorCode.LazyWithSetter, 'Lazy is only applicable if no setter is specified!');
  }
}

export class WrongAuthenticatorSecretError extends ClientCoreDomainError {
  constructor() {
    super(
      ClientCoreErrorCode.WrongAuthenticatorSecret,
      'Tried to unlock authenticator with wrong authenticator secret!',
    );
  }
}

export class SecretReuseError extends ClientCoreDomainError {
  constructor() {
    super(
      ClientCoreErrorCode.SecretReuse,
      "Can't reuse secret that is already associated with an authenticator",
    );
  }
}

export class InvalidRecoveryCodeError extends ClientCoreDomainError {
  constructor() {
    super(ClientCoreErrorCode.InvalidRecoveryCode, 'Incorrect recovery code!');
  }
}

export class InvalidAuthenticatorSeedLengthError extends ClientCoreDomainError {
  constructor() {
    super(
      ClientCoreErrorCode.InvalidAuthenticatorSeedLength,
      'Authenticator seed length must be 32 bytes!',
    );
  }
}

export class MetaVaultIdDoesntMatchError extends ClientCoreDomainError {
  constructor() {
    super(ClientCoreErrorCode.MetaVaultIdDoesntMatch, 'metaVaultId must match metaVault.id!');
  }
}

export class PrivateLoginVaultNotFoundError extends ClientCoreDomainError {
  constructor() {
    super(ClientCoreErrorCode.PrivateLoginVaultNotFound, 'No initialized private login vault!');
  }
}

export class MetaVaultNotFoundError extends ClientCoreDomainError {
  constructor() {
    super(ClientCoreErrorCode.MetaVaultNotFound, 'No initialized meta vault!');
  }
}

export class VaultNotFoundError extends ClientCoreDomainError {
  constructor(vaultId: string) {
    super(ClientCoreErrorCode.VaultNotFound, `No Vault with id ${vaultId} in VaultRepo`);
  }
}

export class OrganizationPersonalVaultNotFoundError extends ClientCoreDomainError {
  constructor(organizationId: string) {
    super(
      ClientCoreErrorCode.OrganizationPersonalVaultNotFound,
      `No personal vault for organization with id  ${organizationId} in VaultRepo`,
    );
  }
}

export class VaultLoginPermissionDeniedError extends ClientCoreDomainError {
  constructor() {
    super(
      ClientCoreErrorCode.VaultLoginPermissionDenied,
      `Account is missing permissions to perform operation on login.`,
    );
  }
}

export class VaultLinkingNotSupportedError extends ClientCoreDomainError {
  constructor(vaultId: string) {
    super(
      ClientCoreErrorCode.VaultLinkingNotSupported,
      `Linking logins not supported for vault ${vaultId}`,
    );
  }
}

export class VaultMessageEncryptedDataNotFoundError extends ClientCoreDomainError {
  constructor() {
    super(
      ClientCoreErrorCode.VaultMessageEncryptedDataNotFound,
      'Encrypted vault message not found',
    );
  }
}

export class IncorrectVaultTypeError extends ClientCoreDomainError {
  constructor(vaultId: string, expected: VaultType, actual: VaultType) {
    super(
      ClientCoreErrorCode.IncorrectVaultTypeError,
      `Expected vault of type ${expected} for id ${vaultId}, got ${actual} instead.`,
    );
  }
}

export class TeamVaultMissingAssociatedMetaVaultError extends ClientCoreDomainError {
  constructor(vaultId: string) {
    super(
      ClientCoreErrorCode.TeamVaultMissingAssociatedMetaVault,
      `Team vault with id ${vaultId} is missing its associated meta vault.`,
    );
  }
}

export class VaultPermissionDeniedError extends ClientCoreDomainError {
  constructor() {
    super(
      ClientCoreErrorCode.VaultPermissionDenied,
      `Account is missing permissions to perform vault operation.`,
    );
  }
}

export class UnsupportedLoginVaultTypeError extends ClientCoreDomainError {
  constructor() {
    super(
      ClientCoreErrorCode.UnsupportedLoginVaultType,
      `Client encountered a login vault with an unsupported type.`,
    );
  }
}

export class InvalidCommitStructureError extends ClientCoreDomainError {
  constructor() {
    super(ClientCoreErrorCode.InvalidCommitStructure, 'Structure of commit to apply is not valid');
  }
}
export class VaultContentTooNewError extends ClientCoreDomainError {
  constructor() {
    super(ClientCoreErrorCode.VaultContentTooNew, 'VaultContent too new, update client!');
  }
}
export class InvalidCommitContentError extends ClientCoreDomainError {
  constructor() {
    super(ClientCoreErrorCode.InvalidCommitContent, 'Content of commit to apply is not valid');
  }
}
export class MergeConflictError extends ClientCoreDomainError {
  constructor() {
    super(ClientCoreErrorCode.MergeConflict, 'Conflict!');
  }
}
export class UnsupportedCompressionError extends ClientCoreDomainError {
  constructor() {
    super(
      ClientCoreErrorCode.UnsupportedCompression,
      'Commit uses unsupported compression algorithm!',
    );
  }
}
export class UnsupportedVaultVersionError extends ClientCoreDomainError {
  constructor() {
    super(ClientCoreErrorCode.UnsupportedVaultVersion, 'Vault version is not supported!');
  }
}

export class VaultIsNotEmptyError extends ClientCoreDomainError {
  constructor() {
    super(ClientCoreErrorCode.VaultIsNotEmpty, 'Vault is not empty');
  }
}

export class AuthenticatorSecreMissingIdError extends ClientCoreDomainError {
  constructor() {
    super(ClientCoreErrorCode.AuthenticatorSecreMissingId, 'Secret must have an authenticatorId');
  }
}

export class AuthenticatorNotFoundError extends ClientCoreDomainError {
  constructor() {
    super(ClientCoreErrorCode.AuthenticatorNotFound, 'Authenticator not found');
  }
}

export class LoginNotInitiatedError extends ClientCoreDomainError {
  constructor() {
    super(
      ClientCoreErrorCode.LoginNotInitiated,
      'Must call initiateLogin() first!',
      'Tried to finish login procedure without initiating it first',
    );
  }
}

export class PushFlowRunningError extends ClientCoreDomainError {
  constructor() {
    super(
      ClientCoreErrorCode.PushFlowRunning,
      'Cannot call more than one operation on PushLoginFlow - cancel first!',
    );
  }
}

export class ChannelIdMissingError extends ClientCoreDomainError {
  constructor() {
    super(ClientCoreErrorCode.ChannelIdMissing, 'Expected push channel id');
  }
}

export class AuthenticatorBlockNotInitialError extends ClientCoreDomainError {
  constructor() {
    super(
      ClientCoreErrorCode.AuthenticatorBlockNotInitial,
      'cannot verify a child AuthenticatorBlock as initial',
    );
  }
}

export class AuthenticatorBlockNotChildError extends ClientCoreDomainError {
  constructor() {
    super(
      ClientCoreErrorCode.AuthenticatorBlockNotChild,
      'cannot verify initial AuthenticatorBlock against a parent',
    );
  }
}

export class AuthenticatorBlockInvalidSigningKeyError extends ClientCoreDomainError {
  constructor() {
    super(
      ClientCoreErrorCode.AuthenticatorBlockInvalidSigningKey,
      'the signing key is not in the AuthenticatorBlock.',
    );
  }
}

export class NoMatchingSigningKeyFoundError extends ClientCoreDomainError {
  constructor() {
    super(ClientCoreErrorCode.NoMatchingSigningKeyFound, 'No key matching the signature');
  }
}

export class NoAuthenticatorIdReceivedError extends ClientCoreDomainError {
  constructor() {
    super(ClientCoreErrorCode.NoAuthenticatorIdReceived, 'no authenticator id received');
  }
}

export class InvalidHashCommitmentError extends ClientCoreDomainError {
  constructor() {
    super(
      ClientCoreErrorCode.InvalidHashCommitment,
      'Client public key does not match the hash commitment',
    );
  }
}

export class LoginCancelledError extends ClientCoreDomainError {
  constructor() {
    super(ClientCoreErrorCode.LoginCancelled, 'Operation was cancelled!');
  }
}

export class UnknownAuthenticatorReplyError extends ClientCoreDomainError {
  constructor() {
    super(ClientCoreErrorCode.UnknownAuthenticatorReply, 'Unknown authenticator reply!');
  }
}

export class OrganizationNotFoundError extends ClientCoreDomainError {
  constructor() {
    super(
      ClientCoreErrorCode.OrganizationNotFoundError,
      'organization not found',
      'Organization not found!',
      `Tried to perform an operation on an unknown organization. ${pleaseUpdate}`,
    );
  }
}

export class RoleNotFoundError extends ClientCoreDomainError {
  constructor() {
    super(
      ClientCoreErrorCode.RoleNotFoundError,
      'role not found',
      'Role not found!',
      `Tried to perform an operation using an unknown role. ${pleaseUpdate}`,
    );
  }
}

export class NoOrganizationMemberError extends ClientCoreDomainError {
  constructor() {
    super(
      ClientCoreErrorCode.NoOrganizationMemberError,
      'no organization member',
      'No organziation member!',
      `Tried to update a user that is not in this organization. ${pleaseUpdate}`,
    );
  }
}

export class NoOrganizationAdminError extends ClientCoreDomainError {
  constructor() {
    super(
      ClientCoreErrorCode.NoOrganizationAdminError,
      'no organization admin',
      'No organization admin!',
      `Tried to manipulate an organization without admin rights. ${pleaseUpdate}`,
    );
  }
}

export class AdminVaultNotFoundError extends ClientCoreDomainError {
  constructor() {
    super(
      ClientCoreErrorCode.AdminVaultNotFoundError,
      'admin vault not fond',
      'Admin vault could not be not found!',
      `Tried to manipulate an organization without the required admin data. ${pleaseUpdate}`,
    );
  }
}

export class NoOrganizationAdminProfileError extends ClientCoreDomainError {
  constructor() {
    super(
      ClientCoreErrorCode.NoOrganizationAdminProfileError,
      'no organization admin profile',
      'This organization has no admin profile',
      pleaseUpdate,
    );
  }
}

export class PrfNotSupportedError extends ClientCoreDomainError {
  constructor() {
    super(
      ClientCoreErrorCode.PrfNotSupportedError,
      'prf not supported',
      'Webauthn PRF extension not supported',
      `Your browser does not support the required PRF extension to perform this operation.`,
    );
  }
}
export class PrfInitError extends ClientCoreDomainError {
  constructor() {
    super(
      ClientCoreErrorCode.PrfInitError,
      'prf init error',
      'PRF initialization failed',
      `Enabling session pairing for this login device failed.`,
    );
  }
}

export class ShareLinkNotFoundError extends ClientCoreDomainError {
  constructor(shareLinkId: string) {
    super(ClientCoreErrorCode.ShareLinkNotFound, `No ShareLink with id ${shareLinkId} found`);
  }
}

export class InvalidShareLinkError extends ClientCoreDomainError {
  constructor() {
    super(
      ClientCoreErrorCode.InvalidShareLink,
      'invalid share link',
      'Invalid share link',
      'This share link is invalid. Please contact the sender to get a new one.',
    );
  }
}

export class InternalRecoveredSyncError extends ClientCoreDomainError {
  constructor() {
    super(ClientCoreErrorCode.InternalRecoveredSync, 'internal recovered sync error');
  }
}

export class CentrifugeError extends ClientCoreDomainError {
  constructor(
    public reason: string,
    public code: number,
  ) {
    super(ClientCoreErrorCode.CentrifugeError, `internal centrifuge error: ${reason} (${code})`);
  }
}

export class ShareSecretNotFoundError extends ClientCoreDomainError {
  constructor(shareSecretId: string) {
    super(ClientCoreErrorCode.ShareSecretNotFound, `No ShareSecret with id ${shareSecretId} found`);
  }
}

export class InvalidShareSecretError extends ClientCoreDomainError {
  constructor() {
    super(ClientCoreErrorCode.InvalidShareSecret, 'invalid share secret', 'Invalid share secret');
  }
}

export class InvalidAgeRecipientError extends ClientCoreDomainError {
  constructor(message: string) {
    super(ClientCoreErrorCode.InvalidAgeRecipient, `Invalid age recipient: ${message}`);
  }
}

export class NotAPasskeyLogin extends ClientCoreDomainError {
  constructor(loginId: LoginId) {
    super(ClientCoreErrorCode.InvalidAgeRecipient, `No passkey login with id ${loginId} found`);
  }
}
